/**
 * Generic scheduling-engine models - the contracts of the backend `schedule` API group
 * (webroot/src/Sensika/Api/v1_0/Schedule/, backend doc: .claude/docs/scheduling-engine.md).
 *
 * RecurrenceDefinition mirrors the backend RecurrenceDefinition VO exactly: it is validated
 * server-side at create/update, so the editor only has to produce this shape.
 */

export type RecurrenceType =
  | 'once'
  | 'interval'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'windowed_interval'
  | 'cron';

export type IntervalUnit = 'minutes' | 'hours' | 'days' | 'weeks';

export interface RecurrenceDefinition {
  type: RecurrenceType;
  /** Absolute unix timestamp - type 'once' only */
  run_at?: number;
  /** type 'interval' / 'windowed_interval' */
  interval_value?: number;
  interval_unit?: IntervalUnit;
  /** type 'interval' only: 'scheduled' (default) or 'completion' */
  anchor?: 'scheduled' | 'completion';
  /** 'HH:MM' in the schedule's timezone - daily/weekly/monthly */
  time_of_day?: string;
  /** ISO weekday numbers 1 (Mon) - 7 (Sun) - weekly/windowed_interval */
  weekdays?: number[];
  /** 1-31, or 0 = last day of the month - monthly only */
  day_of_month?: number;
  /** 'HH:MM' - windowed_interval only */
  window_start_time?: string;
  window_end_time?: string;
  /** Standard 5-field cron expression - type 'cron' only */
  cron_expression?: string;
  /** Unix timestamps bounding the produced occurrences */
  start_date?: number;
  end_date?: number;
}

/** Body of POST /schedule/create and PATCH /schedule/{id}/update */
export interface ScheduleDefinitionInput {
  title: string;
  description?: string;
  action_type_id: number;
  action_params?: Record<string, unknown>;
  recurrence: RecurrenceDefinition;
  timezone_id?: number;
  misfire_policy_id?: number;
  allow_overlap?: boolean;
  max_attempts?: number;
  company_id?: string;
  context_object_id?: string;
  context_object_class_id?: number;
}

export interface NomRef {
  id: number;
  name: string;
}

export interface TimeInfo {
  timestamp: number;
  absolute: string;
  relative: string;
}

export interface ScheduleLatestRun {
  schedule_run_id: number;
  scheduled_time: TimeInfo;
  status: NomRef | null;
  done_at: TimeInfo;
  attempt_count: number;
  last_error: string | null;
}

/** Row shape produced by the backend ScheduleTransformer */
export interface EngineSchedule {
  schedule_id: string;
  title: string;
  description: string | null;
  status: NomRef | null;
  action_type: NomRef | null;
  recurrence_type: NomRef | null;
  recurrence_summary: string;
  recurrence_definition: RecurrenceDefinition | null;
  action_params: Record<string, unknown> | null;
  misfire_policy: NomRef | null;
  allow_overlap: boolean;
  max_attempts: number;
  timezone_id: number | null;
  company_id: string | null;
  context_object_id: string | null;
  context_object_class_id: number;
  next_run: TimeInfo;
  last_run: TimeInfo;
  last_run_status: NomRef | null;
  consecutive_failures: number;
  latest_run: ScheduleLatestRun | null;
  can_pause: boolean;
  can_resume: boolean;
  can_run_now: boolean;
  time_created: TimeInfo;
  time_updated: TimeInfo;
}

/** Row shape produced by the backend ScheduleRunTransformer */
export interface EngineScheduleRun {
  schedule_run_id: number;
  schedule_id: string;
  scheduled_time: TimeInfo;
  origin: NomRef | null;
  status: NomRef | null;
  action_type: NomRef | null;
  claimed_at: TimeInfo;
  processing_started_at: TimeInfo;
  done_at: TimeInfo;
  attempt_count: number;
  last_error: string | null;
  snapshot: Record<string, unknown>;
  time_created: TimeInfo;
}

/** nom_schedule_misfire_policy ids (seeded by the engine) */
export enum NomScheduleMisfirePolicy {
  FIRE_ONCE = 1,
  SKIP = 2,
}
